import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Sun, Moon, Eye, EyeOff, Mail, Lock, User, ArrowLeft, Building2, Users, ArrowRight, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = ({ light }) => (
  <div className="woxora-logo text-3xl">
    <span className="wox" style={light ? { color: '#F2EFE9' } : undefined}>Wox</span>
    <span className="ora">ora</span>
  </div>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const { login: googleLogin, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resendInvite, setResendInvite] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [inviteForm, setInviteForm] = useState({ fullName: '', password: '', confirmPassword: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ strength: '', score: 0 });

  const checkPasswordStrength = async (password) => {
    if (password.length < 3) {
      setPasswordStrength({ strength: '', score: 0 });
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/password-strength`, { params: { password } });
      const strength = response.data.strength;
      const scores = { weak: 33, fair: 66, strong: 100 };
      setPasswordStrength({ strength, score: scores[strength] || 0 });
    } catch (error) {
      console.error('Error checking password:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginForm.email,
        password: loginForm.password
      }, { withCredentials: true });
      setUser(response.data);
      toast.success('Welcome back!');
      // Login → always redirect to dashboard (or onboarding if not completed)
      if (response.data.onboarding_completed) {
        navigate('/dashboard', { replace: true, state: { user: response.data } });
      } else {
        navigate('/onboarding', { replace: true, state: { user: response.data } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.fullName || !inviteForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (inviteForm.password !== inviteForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordStrength.strength === 'weak') {
      toast.error('Please choose a stronger password');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/accept-invite`, null, {
        params: {
          token: inviteToken,
          password: inviteForm.password,
          full_name: inviteForm.fullName
        }
      });
      toast.success('Account created! Signing you in...');
      
      // Auto-login after invite acceptance
      try {
        // Get the invite email from the token
        const inviteCheck = await axios.get(`${API}/team/invite-info`, {
          params: { token: inviteToken }
        }).catch(() => null);
        
        // Navigate to login since we don't have the email readily available
        // The user just needs to sign in with their new credentials
        navigate('/auth', { replace: true });
        toast.info('Please sign in with your new credentials');
      } catch {
        navigate('/auth', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If the email exists, a reset link has been sent');
      setForgotPassword(false);
    } catch (error) {
      toast.error('Something went wrong');
    }
    setLoading(false);
  };

  const handleResendInvite = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/team/resend-invite-by-email`, { email: resendEmail }, { withCredentials: true });
      toast.success('If you have a pending invitation, a new link has been sent');
      setResendInvite(false);
    } catch (error) {
      // Always show success to prevent email enumeration
      toast.success('If you have a pending invitation, a new link has been sent');
      setResendInvite(false);
    }
    setLoading(false);
  };

  const getStrengthColor = () => {
    switch (passwordStrength.strength) {
      case 'weak': return 'bg-red-500';
      case 'fair': return 'bg-amber-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-slate-200';
    }
  };

  // FORGOT PASSWORD VIEW
  if (forgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--woxora-bg)' }}>
        <Card className="w-full max-w-md">
          <CardHeader>
            <button 
              onClick={() => setForgotPassword(false)}
              className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </button>
            <CardTitle>Reset Password</CardTitle>
            <CardDescription>
              Enter your email and we'll send you a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-10"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#E06D53] hover:bg-[#c95a42]" disabled={loading} data-testid="forgot-submit">
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // RESEND INVITATION VIEW
  if (resendInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--woxora-bg)' }}>
        <Card className="w-full max-w-md">
          <CardHeader>
            <button 
              onClick={() => setResendInvite(false)}
              className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <CardTitle>Resend Invitation</CardTitle>
            <CardDescription>
              Enter your work email to receive a new invitation link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResendInvite} className="space-y-4">
              <div>
                <Label htmlFor="resend-email">Work Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="resend-email"
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-10"
                    data-testid="resend-email-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#E06D53] hover:bg-[#c95a42]" disabled={loading} data-testid="resend-submit">
                {loading ? 'Sending...' : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Resend Invitation
                  </>
                )}
              </Button>
            </form>
            <p className="text-xs text-center text-slate-500 mt-4">
              Don't have an invitation? Ask your company admin to send you one.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // INVITE ACCEPTANCE VIEW
  if (inviteToken) {
    return (
      <div className="min-h-screen flex" style={{ backgroundColor: 'var(--woxora-bg)' }}>
        <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden" style={{ backgroundColor: '#0A0A0B' }}>
          <div className="absolute -right-40 -bottom-40 w-96 h-96 rounded-full blur-3xl" style={{ background: 'rgba(224, 109, 83, 0.12)' }} />
          <div className="relative z-10">
            <Logo light />
            <p className="text-sm mt-2" style={{ color: '#555', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Where business gets done.</p>
          </div>
          <div className="relative z-10">
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#F2EFE9', letterSpacing: '-0.02em', lineHeight: '1.15' }}>
              You've been invited to join Woxora
            </h1>
            <p className="text-lg" style={{ color: '#777', fontFamily: 'Outfit, sans-serif' }}>
              Set up your password to join your team's workspace.
            </p>
          </div>
          <div className="relative z-10">
            <div className="rounded-2xl p-6" style={{ backgroundColor: '#141415', border: '1px solid #242426' }}>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>500+</p>
                  <p className="text-sm" style={{ color: '#666' }}>Companies</p>
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>10K+</p>
                  <p className="text-sm" style={{ color: '#666' }}>Users</p>
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>99.9%</p>
                  <p className="text-sm" style={{ color: '#666' }}>Uptime</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="flex items-center justify-between mb-8">
              <div className="lg:hidden"><Logo /></div>
              <Button variant="ghost" size="icon" onClick={toggleTheme} className="ml-auto">
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
            </div>
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl" data-testid="invite-title">Join Your Team</CardTitle>
                <CardDescription>Create your password to complete setup</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAcceptInvite} className="space-y-4">
                  <div>
                    <Label htmlFor="invite-name">Full Name</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="invite-name"
                        value={inviteForm.fullName}
                        onChange={(e) => setInviteForm({ ...inviteForm, fullName: e.target.value })}
                        placeholder="John Doe"
                        className="pl-10"
                        data-testid="invite-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="invite-password">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="invite-password"
                        type={showPassword ? 'text' : 'password'}
                        value={inviteForm.password}
                        onChange={(e) => {
                          setInviteForm({ ...inviteForm, password: e.target.value });
                          checkPasswordStrength(e.target.value);
                        }}
                        placeholder="Create a strong password"
                        className="pl-10 pr-10"
                        data-testid="invite-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {inviteForm.password && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-500">Password strength</span>
                          <span className={`font-medium capitalize ${
                            passwordStrength.strength === 'weak' ? 'text-red-500' :
                            passwordStrength.strength === 'fair' ? 'text-amber-500' :
                            passwordStrength.strength === 'strong' ? 'text-green-500' : ''
                          }`}>{passwordStrength.strength}</span>
                        </div>
                        <Progress value={passwordStrength.score} className={`h-1.5 ${getStrengthColor()}`} />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="invite-confirm">Confirm Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="invite-confirm"
                        type="password"
                        value={inviteForm.confirmPassword}
                        onChange={(e) => setInviteForm({ ...inviteForm, confirmPassword: e.target.value })}
                        placeholder="Confirm your password"
                        className="pl-10"
                        data-testid="invite-confirm"
                      />
                    </div>
                    {inviteForm.confirmPassword && inviteForm.password !== inviteForm.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#E06D53] hover:bg-[#c95a42]"
                    disabled={loading}
                    data-testid="invite-submit"
                  >
                    {loading ? 'Creating account...' : 'Join Team'}
                  </Button>
                </form>
                <p className="text-xs text-center text-slate-500 mt-6">
                  Already have an account?{' '}
                  <button onClick={() => navigate('/auth')} className="text-[#E06D53] hover:underline" data-testid="invite-login-link">Sign in</button>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // DEFAULT LOGIN VIEW
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--woxora-bg)' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden" style={{ backgroundColor: '#0A0A0B' }}>
        <div className="absolute -right-40 -bottom-40 w-96 h-96 rounded-full blur-3xl" style={{ background: 'rgba(224, 109, 83, 0.12)' }} />
        <div className="relative z-10">
          <Logo light />
          <p className="text-sm mt-2" style={{ color: '#555', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Where business gets done.</p>
        </div>
        <div className="relative z-10">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#F2EFE9', letterSpacing: '-0.02em', lineHeight: '1.15' }}>
            Your entire business intelligence, powered by AI
          </h1>
          <p className="text-lg" style={{ color: '#777', fontFamily: 'Outfit, sans-serif' }}>
            Daily work reports, complaint resolution, and sales tracking — all automated and intelligent.
          </p>
        </div>
        <div className="relative z-10">
          <div className="rounded-2xl p-6" style={{ backgroundColor: '#141415', border: '1px solid #242426' }}>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>500+</p>
                <p className="text-sm" style={{ color: '#666' }}>Companies</p>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>10K+</p>
                <p className="text-sm" style={{ color: '#666' }}>Users</p>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>99.9%</p>
                <p className="text-sm" style={{ color: '#666' }}>Uptime</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden"><Logo /></div>
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="ml-auto">
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl" data-testid="auth-title">Welcome to Woxora</CardTitle>
              <CardDescription>Sign in to your account</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Google OAuth Button */}
              <Button
                variant="outline"
                className="w-full mb-6 h-12"
                onClick={googleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center"><Separator /></div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                </div>
              </div>

              {/* Login Form */}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-email"
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      placeholder="you@company.com"
                      className="pl-10"
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button
                      type="button"
                      onClick={() => setForgotPassword(true)}
                      className="text-xs text-[#E06D53] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      placeholder="Enter your password"
                      className="pl-10 pr-10"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#E06D53] hover:bg-[#c95a42]"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : 'Sign In'}
                </Button>
              </form>

              <Separator className="my-6" />

              {/* Two Options for New Users */}
              <div className="space-y-3">
                <p className="text-sm text-center text-slate-600 dark:text-slate-400 font-medium">
                  Don't have an account?
                </p>
                
                {/* Option 1: Business Owner - Start Free Trial */}
                <Link to="/signup">
                  <Button variant="outline" className="w-full h-12 justify-start px-4" data-testid="start-trial-btn">
                    <Building2 className="w-5 h-5 mr-3 text-[#E06D53]" />
                    <div className="text-left flex-1">
                      <p className="font-medium">Start Free Trial</p>
                      <p className="text-xs text-slate-500">For business owners — create your company</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </Button>
                </Link>
                
                {/* Option 2: Employee - I have an invitation */}
                <Button 
                  variant="outline" 
                  className="w-full h-12 justify-start px-4"
                  onClick={() => setResendInvite(true)}
                  data-testid="have-invite-btn"
                >
                  <Users className="w-5 h-5 mr-3 text-[#E06D53]" />
                  <div className="text-left flex-1">
                    <p className="font-medium">I have an invitation</p>
                    <p className="text-xs text-slate-500">For employees — resend invitation link</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </Button>
              </div>

              <p className="text-xs text-center text-slate-500 mt-6">
                By continuing, you agree to our{' '}
                <a href="#" className="text-[#E06D53] hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-[#E06D53] hover:underline">Privacy Policy</a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
